import { createPortal } from 'react-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  ExternalLink,
  Pencil,
  Download,
  Trash2,
  FileText,
  Loader2,
  RotateCcw,
  Sparkles,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { Link, useParams } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { Input } from '@deepguide-ai/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import type { VendorTrustDocLink } from '@deepguide-ai/types'
import { EvidenceFilePreviewDialog } from '@/components/auditor-center/EvidenceFilePreviewDialog'
import { VendorTrustApiCard } from './VendorTrustApiCard'
import { formatDate } from '@/lib/format'
import { formatFileSize } from '@/utils/format-file-size'
import { useVendorDocumentUpload, invalidateVendorDocumentQueries } from './useVendorDocumentUpload'
import { useVendorPageSnapshot } from './VendorTrustLinksList'
import { DOC_TYPE_LABELS } from '@/pages/vendors/doc-type-labels'
import {
  buildDocumentRows,
  getEvidenceCompleteness,
  type EvidenceRequest,
  type VendorDocument,
} from './vendorAssessmentDocumentRows'

/**
 * RFC-231 §3.1 — one list for vendor sources, files and review selection.
 *
 * The tier checklist (RFC-196 evidence requests) and the document selection
 * (RFC-198 assessment inputs) are two answers to one question — what does this
 * review have, and what is missing — so they are one list. Each expected
 * document is either satisfied by a stash document (ticked, Received) or
 * outstanding, in which case the row carries where to get it (RFC-231 §6),
 * Upload, Save snapshot and Waive. Documents that are not on the checklist
 * follow as extras.
 */

const STATUS_BADGE = {
  RECEIVED: { label: 'Received', status: 'green' as const },
  WAIVED: { label: 'Waived', status: 'gray' as const },
  OUTSTANDING: { label: 'Outstanding', status: 'amber' as const },
}

export function VendorAssessmentInputsPanel({
  assessmentId,
  vendorId,
  vendorName,
  isEditable,
  aiEnabled = false,
  evidenceRequests = [],
  onAssessed = () => {},
  onBeforeAssess,
  assessmentActionContainer,
  assessmentActionLabel,
  onChanged,
}: {
  assessmentId?: string
  vendorId: string
  vendorName?: string
  isEditable: boolean
  aiEnabled?: boolean
  evidenceRequests?: EvidenceRequest[]
  assessmentActionContainer?: HTMLElement | null
  assessmentActionLabel?: string
  onBeforeAssess?: () => Promise<void>
  onAssessed?: () => void | Promise<void>
  /** Called after an intake change (upload, snapshot, waive, reopen). */
  onChanged: () => void
}) {
  const { workspaceId } = useParams({ strict: false })
  const queryClient = useQueryClient()
  const documentsQuery = useQuery(
    trpc.vendorManagement.listDocuments.queryOptions({ vendorId })
  )
  const inputsQuery = useQuery(
    { ...trpc.vendorAssessment.listInputs.queryOptions({ assessmentId: assessmentId ?? '' }), enabled: Boolean(assessmentId) }
  )
  // RFC-231 §6 — where each outstanding document lives (catalog data only).
  const trustLinksQuery = useQuery(
    trpc.vendorManagement.trustLinks.queryOptions({ vendorId })
  )
  const [editingLinkType, setEditingLinkType] = useState<string | null>(null)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkAccess, setLinkAccess] = useState<VendorTrustDocLink['access']>('public')
  const [linkHow, setLinkHow] = useState('')
  const setTrustLinkMutation = useMutation(trpc.vendorManagement.setTrustLink.mutationOptions())
  const deleteDocumentMutation = useMutation(trpc.vendorManagement.deleteDocument.mutationOptions())
  const setInputsMutation = useMutation(
    trpc.vendorAssessment.setInputs.mutationOptions()
  )
  const assessMutation = useMutation(
    trpc.vendorAssessment.assess.mutationOptions()
  )
  const waiveMutation = useMutation(
    trpc.vendorAssessment.waiveEvidenceRequest.mutationOptions()
  )
  const reopenMutation = useMutation(
    trpc.vendorAssessment.reopenEvidenceRequest.mutationOptions()
  )
  const updateDocTypeMutation = useMutation(
    trpc.vendorManagement.updateDocumentType.mutationOptions()
  )

  const documents = (documentsQuery.data ?? []) as VendorDocument[]
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [initialized, setInitialized] = useState(false)
  // Click a document title → preview it in the shared evidence viewer.
  const [previewFile, setPreviewFile] = useState<{
    url: string
    filename: string
    mimeType: string | null
  } | null>(null)
  const openPreview = async (doc: { id: string; fileName: string | null; title: string }) => {
    try {
      const result = await queryClient.fetchQuery(
        trpc.vendorManagement.getDocumentDownloadUrl.queryOptions({ documentId: doc.id })
      )
      const url = result?.previewUrl ?? result?.url
      if (!url) {
        toast.error('No file on this document')
        return
      }
      setPreviewFile({
        url,
        filename: doc.fileName || doc.title,
        mimeType: null, // viewer falls back to the filename extension
      })
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] preview failed', err)
      toast.error('Could not open the document')
    }
  }
  const [dragActive, setDragActive] = useState(false)
  const [waivingId, setWaivingId] = useState<string | null>(null)
  const [waiveReason, setWaiveReason] = useState('')
  // Latest selection + a debounce timer, so a burst of toggles collapses into
  // one write of the final set (avoids stale-closure / out-of-order persists).
  const selectedRef = useRef(selected)
  const saveTimer = useRef<number | null>(null)
  useEffect(() => {
    selectedRef.current = selected
  }, [selected])
  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
    },
    []
  )
  // Load the assessment's saved selection (seeded server-side at creation).
  useEffect(() => {
    if (initialized || !inputsQuery.data) return
    setSelected(
      new Set(
        (inputsQuery.data as any[]).map((i) => i.vaultDocumentId).filter(Boolean)
      )
    )
    setInitialized(true)
  }, [inputsQuery.data, initialized])

  const persistSelection = async (ids: string[]) => {
    if (!assessmentId) return
    try {
      await (setInputsMutation as any).mutateAsync({
        assessmentId,
        vaultDocumentIds: ids,
      })
      queryClient.invalidateQueries({
        queryKey: assessmentId ? trpc.vendorAssessment.listInputs.queryKey({ assessmentId }) : trpc.vendorAssessment.listInputs.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.vendorManagement.listDocuments.queryKey({ vendorId }),
      })
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] save selection failed', err)
      toast.error(err instanceof Error ? err.message : 'Could not save selection')
    }
  }

  const toggle = (docId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(docId)) next.delete(docId)
      else next.add(docId)
      return next
    })
    // Debounce: persist the latest set once the burst settles.
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => {
      void persistSelection([...selectedRef.current])
    }, 400)
  }

  const selectedIds = useMemo(() => [...selected], [selected])
  const trustDocs = useMemo<Record<string, VendorTrustDocLink>>(() => ({
    ...(trustLinksQuery.data?.trustCenterUrl ? { trust_center: { url: trustLinksQuery.data.trustCenterUrl, access: 'public' as const } } : {}),
    ...(trustLinksQuery.data?.docs ?? {}),
  }), [trustLinksQuery.data])
  const rows = useMemo(
    () => buildDocumentRows(evidenceRequests, documents, Object.fromEntries(
      [...new Set(['trust_center', 'soc2_report', 'dpa', ...Object.keys(trustDocs), ...(trustLinksQuery.data?.sourceTypes ?? [])])].map(type => [type, DOC_TYPE_LABELS[type] ?? type])
    )),
    [evidenceRequests, documents, trustDocs, trustLinksQuery.data?.sourceTypes]
  )
  const completeness = getEvidenceCompleteness(evidenceRequests)
  const requestRows = rows.filter((r) => r.request || r.source)
  const extraRows = rows.filter((r) => !r.request && !r.source)

  // Upload straight into the review. The document type is inferred from the
  // filename server-side unless the row already knows what it is asking for.
  const { upload, uploading } = useVendorDocumentUpload(vendorId)
  const handleUpload = async (files: FileList | null, documentType?: string) => {
    if (!files?.length) return
    try {
      const { uploaded, evidenceRequestsSatisfied } = await upload(files, {
        assessmentId,
        ...(documentType ? { documentType } : {}),
      })
      if (uploaded === 0) return
      toast.success(
        evidenceRequestsSatisfied > 0
          ? `Uploaded ${uploaded} document(s) · ${evidenceRequestsSatisfied} evidence request(s) received`
          : `Uploaded ${uploaded} document(s)`
      )
      onChanged()
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] upload failed', err)
      toast.error(err instanceof Error ? err.message : 'Upload failed')
    }
  }

  const { saveSnapshot, busyKey: snapshotBusyKey } = useVendorPageSnapshot(
    vendorId,
    vendorName
  )

  const handleWaive = async (requestId: string) => {
    try {
      await (waiveMutation as any).mutateAsync({
        requestId,
        reason: waiveReason.trim(),
      })
      setWaivingId(null)
      setWaiveReason('')
      onChanged()
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] waive failed', err)
      toast.error(err instanceof Error ? err.message : 'Could not waive request')
    }
  }

  // Uploads and drops infer the type from the filename, so anything it cannot
  // recognise lands as `other`. Correcting it here is what actually identifies
  // the document: the server re-points the evidence request it satisfies, so
  // the row moves onto its checklist entry.
  const handleDocTypeChange = async (documentId: string, documentType: string) => {
    try {
      await (updateDocTypeMutation as any).mutateAsync({ documentId, documentType })
      queryClient.invalidateQueries({
        queryKey: trpc.vendorManagement.listDocuments.queryKey({ vendorId }),
      })
      queryClient.invalidateQueries({
        queryKey: assessmentId ? trpc.vendorAssessment.listInputs.queryKey({ assessmentId }) : trpc.vendorAssessment.listInputs.queryKey(),
      })
      onChanged()
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] document type update failed', err)
      toast.error(err instanceof Error ? err.message : 'Could not update type')
    }
  }

  const handleReopen = async (requestId: string) => {
    try {
      await (reopenMutation as any).mutateAsync({ requestId })
      onChanged()
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] reopen failed', err)
      toast.error(err instanceof Error ? err.message : 'Could not reopen request')
    }
  }

  const [startingAssessment, setStartingAssessment] = useState(false)
  const assessmentInFlight = useRef(false)
  const handleAssess = async () => {
    if (!assessmentId || !isEditable || !aiEnabled || assessmentInFlight.current) return
    // Cancel any pending debounced save — this explicit write supersedes it.
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    assessmentInFlight.current = true
    setStartingAssessment(true)
    try {
      await onBeforeAssess?.()
      // Persist the current selection (flushing any in-flight toggle), then
      // run the batch assessment over it.
      await (setInputsMutation as any).mutateAsync({
        assessmentId,
        vaultDocumentIds: selectedIds,
      })
      const result = await (assessMutation as any).mutateAsync({ assessmentId })
      const failed = (result?.perDocument ?? []).filter((d: any) => !d.ok)
      if (failed.length > 0) {
        toast.warning(
          `Assessed ${selectedIds.length - failed.length}/${selectedIds.length} documents · ${failed.length} could not be read`
        )
      } else {
        toast.success(selectedIds.length ? `Assessed ${selectedIds.length} document(s)` : 'Assessed from vendor context without supporting documents')
      }
      await onAssessed()
    } catch (err) {
      console.error('[VendorAssessmentInputsPanel] assess failed', err)
      toast.error(err instanceof Error ? err.message : 'Assessment failed')
    } finally {
      assessmentInFlight.current = false
      setStartingAssessment(false)
    }
  }

  const handleDownload = async (doc: VendorDocument) => {
    try {
      const result = await queryClient.fetchQuery(trpc.vendorManagement.getDocumentDownloadUrl.queryOptions({ documentId: doc.id }))
      if (!result?.url) throw new Error('No file on this document')
      const link = document.createElement('a')
      link.href = result.url
      link.download = doc.fileName || doc.title
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not download document')
    }
  }
  const handleDelete = async (documentId: string) => {
    try {
      await deleteDocumentMutation.mutateAsync({ documentId })
      setSelected(previous => {
        const next = new Set(previous)
        next.delete(documentId)
        selectedRef.current = next
        return next
      })
      invalidateVendorDocumentQueries(queryClient, vendorId, assessmentId)
      onChanged()
      toast.success('Document removed')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove document')
    }
  }

  const renderRow = ({ key, request, source, doc }: (typeof rows)[number]) => {
    const docType = request?.docType ?? source?.docType ?? doc?.documentType ?? 'other'
    const label = doc ? doc.fileName || doc.title : request?.label ?? source?.label
    const trustLink = request || source ? trustDocs[docType] : undefined
    const badge = request ? STATUS_BADGE[request.status] : null
    return <li key={key} className='grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-3 gap-y-2 py-3 sm:grid-cols-[1.25rem_minmax(0,1fr)_auto]'>
      {doc && assessmentId ? <Checkbox
        checked={selected.has(doc.id)} onCheckedChange={() => toggle(doc.id)}
        disabled={!isEditable || inputsQuery.isLoading} aria-label={`Include ${label} in this review`}
      /> : <FileText aria-hidden='true' className='text-text-subtle mt-0.5 size-4' />}
      <div className='min-w-0 space-y-1'>
        <div className='flex flex-wrap items-center gap-2'>
          {doc ? <button type='button' title={`Preview ${label}`} onClick={() => void openPreview(doc)} className='text-text-secondary truncate text-left text-[12px] font-medium hover:text-cobalt-strong hover:underline'>{label}</button>
            : <span className='text-text-secondary text-[12px] font-medium'>{label}{request && !request.required ? ' (optional)' : ''}</span>}
          {badge && <Badge status={badge.status} className='h-5 px-2 text-[10px]'>{badge.label}</Badge>}
          {doc?.source === 'trust-api' && <Badge status={doc.trustApi?.attestedMatch === false ? 'amber' : 'blue'} title={doc.trustApi ? `Fetched from ${doc.trustApi.publisherName ?? doc.trustApi.apiBase} · ${doc.trustApi.fetchedAt} · signed snapshot ${doc.trustApi.attestedMatch === true ? 'verified' : doc.trustApi.attestedMatch === false ? 'MISMATCH' : 'not available'}` : 'Fetched from Trust API'}>Trust API</Badge>}
          {doc?.snapshot && <Badge status='blue' title={`Snapshot of ${doc.snapshot.sourceUrl} · ${doc.snapshot.fetchedAt} · sha256 ${doc.snapshot.sha256}`}>Snapshot</Badge>}
        </div>
        {trustLink && <>
          <a href={trustLink.url} target='_blank' rel='noreferrer' className='text-cobalt-strong inline-flex max-w-full items-center gap-1 break-all text-[11px] hover:underline'>
            {trustLink.url.replace(/^https?:\/\//, '')}<ExternalLink className='size-3 shrink-0' />
          </a>
          {trustLink.access !== 'public' && <span className='text-text-subtle ml-2 text-[10px]'>{trustLink.access === 'portal' ? 'Customer portal' : 'Request access'}</span>}
          {trustLink.how && <p className='text-text-subtle text-[11px]'>{trustLink.how}</p>}
        </>}
        {isEditable && (request || source) && editingLinkType !== docType && <Button size='smIcon' variant='ghost' aria-label={`${trustLink ? 'Edit' : 'Add'} ${DOC_TYPE_LABELS[docType] ?? docType} link`}
          onClick={() => { setEditingLinkType(docType); setLinkUrl(trustLink?.url ?? ''); setLinkAccess(trustLink?.access ?? 'public'); setLinkHow(trustLink?.how ?? '') }}><Pencil className='size-3' /></Button>}
        {isEditable && editingLinkType === docType && (request || source) && <div className='space-y-2'>
          <Input aria-label={`${DOC_TYPE_LABELS[docType] ?? docType} source URL`} placeholder='https://vendor.com/document' value={linkUrl} onChange={event => setLinkUrl(event.target.value)} />
          <Select value={linkAccess} onValueChange={value => setLinkAccess(value as VendorTrustDocLink['access'])}>
            <SelectTrigger aria-label='Link access' fullWidth><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value='public'>Public</SelectItem><SelectItem value='portal'>Customer portal</SelectItem><SelectItem value='request'>Request access</SelectItem></SelectContent>
          </Select>
          {linkAccess !== 'public' && <Input aria-label='How to obtain the document' placeholder='How to obtain the document' value={linkHow} onChange={event => setLinkHow(event.target.value)} />}
          <div className='flex gap-2'>
            <Button size='sm' disabled={setTrustLinkMutation.isPending} onClick={async () => {
              try {
                await setTrustLinkMutation.mutateAsync({ vendorId, documentType: docType as VendorDocument['documentType'], link: linkUrl.trim() ? { url: linkUrl.trim(), access: linkAccess, ...(linkAccess !== 'public' ? { how: linkHow.trim() } : {}) } : null })
                await queryClient.invalidateQueries({ queryKey: trpc.vendorManagement.trustLinks.queryKey({ vendorId }) })
                setEditingLinkType(null)
                onChanged()
              } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not save source link') }
            }}>Save link</Button>
            <Button size='sm' variant='ghost' disabled={setTrustLinkMutation.isPending} onClick={() => setEditingLinkType(null)}>Cancel</Button>
          </div>
        </div>}
      </div>
      <div className='col-start-2 flex flex-wrap items-center gap-1 sm:col-start-3 sm:justify-end'>
        {trustLink?.access === 'public' && isEditable && <Button size='sm' variant='outline' className='h-7 text-[11px]' disabled={snapshotBusyKey !== null || busy}
          onClick={() => void saveSnapshot(docType, trustLink, onChanged)} aria-label={`Save ${DOC_TYPE_LABELS[docType] ?? docType} snapshot`}>
          {snapshotBusyKey === docType && <Loader2 className='size-3 animate-spin' />}Save snapshot
        </Button>}
        {isEditable && <Button asChild size='sm' variant='outline' className={`h-7 text-[11px] ${busy ? 'pointer-events-none opacity-50' : ''}`}>
          <label className='cursor-pointer' role='button' aria-label={`Upload ${DOC_TYPE_LABELS[docType] ?? docType}`} tabIndex={busy ? -1 : 0} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.querySelector('input')?.click() } }}><Upload className='size-3' />{doc ? 'Add file' : 'Upload'}
            <input type='file' className='hidden' aria-label={`${DOC_TYPE_LABELS[docType] ?? docType} file`} disabled={busy} onChange={event => { void handleUpload(event.target.files, docType); event.target.value = '' }} />
          </label>
        </Button>}
        {doc && <Button size='smIcon' variant='ghost' aria-label={`Download ${label}`} onClick={() => void handleDownload(doc)}><Download className='size-3.5' /></Button>}
        {doc && isEditable && <Button size='smIcon' variant='ghost' aria-label={`Delete ${label}`} disabled={busy} onClick={() => void handleDelete(doc.id)}><Trash2 className='size-3.5' /></Button>}
        {isEditable && request?.status === 'OUTSTANDING' && <Button size='sm' variant='ghost' className='h-7 text-[11px]' disabled={busy} onClick={() => { setWaivingId(request.id); setWaiveReason('') }}><Ban className='size-3' />Waive</Button>}
        {isEditable && request?.status === 'WAIVED' && <Button size='sm' variant='ghost' className='h-7 text-[11px]' disabled={busy} onClick={() => void handleReopen(request.id)}><RotateCcw className='size-3' />Reopen</Button>}
      </div>
      {doc && <div className='col-start-2 flex flex-wrap items-center gap-2 sm:col-end-4'>
        {isEditable ? <Select value={doc.documentType} onValueChange={value => void handleDocTypeChange(doc.id, value)} disabled={updateDocTypeMutation.isPending}>
          <SelectTrigger className='h-6 w-40 text-[10px]' aria-label={`Document type for ${label}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(DOC_TYPE_LABELS).map(([value, text]) => <SelectItem key={value} value={value}>{text}</SelectItem>)}</SelectContent>
        </Select> : <span className='text-text-subtle text-[10px]'>{DOC_TYPE_LABELS[docType] ?? docType}</span>}
        {doc.uploadedAt && <span className='text-text-subtle text-[10px]'>{formatDate(doc.uploadedAt)}{doc.uploadedByName ? ` · ${doc.uploadedByName}` : ''}</span>}
        <span className='text-text-subtle text-[10px]'>{formatFileSize(doc.fileSize)} · {doc.status === 'ARCHIVED' ? 'Archived' : doc.effectiveTo ? `Until ${formatDate(doc.effectiveTo)}` : 'Active'}</span>
        {doc.assessments?.map(review => <Link key={review.id} to='/workspaces/$workspaceId/vendors/$vendorId/assessments/$assessmentId' params={{ workspaceId: workspaceId!, vendorId, assessmentId: review.id }} className='text-cobalt-strong text-[10px] hover:underline'>Assessment v{review.version}</Link>)}
      </div>}
      {(request?.docType === 'trust_center' || source?.docType === 'trust_center') && <details className='col-start-2 text-[11px] sm:col-end-4'>
        <summary className='text-text-subtle cursor-pointer'>Connect Trust Center API</summary>
        <div className='mt-3'><VendorTrustApiCard vendorId={vendorId} readOnly={!isEditable} onDocumentsChanged={onChanged} /></div>
      </details>}
      {request?.status === 'WAIVED' && request.waivedReason && <p className='text-text-subtle col-start-2 text-[11px] sm:col-end-4'>Waived: {request.waivedReason}</p>}
      {request && waivingId === request.id && <div className='col-start-2 flex flex-wrap items-center gap-2 sm:col-end-4'>
        <Input autoFocus value={waiveReason} onChange={event => setWaiveReason(event.target.value)} placeholder='Why is this document not needed?' aria-label='Waiver reason' className='h-8 min-w-40 flex-1 text-[11px]' />
        <Button size='sm' disabled={!waiveReason.trim() || busy} onClick={() => void handleWaive(request.id)}>Record waiver</Button>
        <Button size='sm' variant='ghost' onClick={() => setWaivingId(null)}>Cancel</Button>
      </div>}
    </li>
  }

  const saving = setInputsMutation.isPending
  const assessing = startingAssessment || assessMutation.isPending
  const busy = uploading || waiveMutation.isPending || reopenMutation.isPending || deleteDocumentMutation.isPending
  const canDrop = isEditable && !uploading


  const assessmentAction = (
    <Button size='sm' variant={assessmentActionLabel === 'Reassess with Vera' ? 'outline' : 'default'} className='h-8 rounded-sm text-[12px]' onClick={handleAssess}
      disabled={assessing || saving || inputsQuery.isLoading || inputsQuery.isError}
      tooltip={selectedIds.length === 0 ? 'Assess from vendor details and your answers without supporting documents' : 'Read the selected documents and draft the questionnaire below'}>
      {assessing ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Sparkles className='h-3.5 w-3.5' />}
      {assessing ? 'Assessing…' : assessmentActionLabel ?? (assessmentActionContainer ? 'Start assessment' : 'Assess')}
    </Button>
  )

  return (
    <section
      aria-label='Vendor document list'
      className={`rounded-sm border bg-card p-4 transition-colors ${
        dragActive ? 'border-cobalt-strong bg-selected' : 'border-border'
      }`}
      onDragOver={(e) => {
        if (!canDrop) return
        e.preventDefault()
        if (!dragActive) setDragActive(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setDragActive(false)
      }}
      onDrop={(e) => {
        if (!canDrop) return
        e.preventDefault()
        setDragActive(false)
        if (e.dataTransfer.files?.length) void handleUpload(e.dataTransfer.files)
      }}
    >
      <div className='mb-3 flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <h3 className='font-body text-text-secondary text-[13px] font-semibold'>
            Vendor documents
          </h3>
          <p className='font-body text-text-subtle text-[11px]'>
            {isEditable ? assessmentId ? 'Upload files or save public sources, or assess from vendor details and your answers without documents.' : 'Open a source link, save a public snapshot, or upload a file.' : 'View vendor documents and source links.'}
          </p>
        </div>
        <div className='flex shrink-0 items-center gap-3'>
          {isEditable && (
            /* asChild forwards to <label>, which has no `disabled` — gate
               pointer events on the label and disable the input instead. */
            <Button
              asChild
              size='sm'
              variant='outline'
              className={`h-8 rounded-sm text-[12px] ${
                uploading ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              <label className='cursor-pointer' role='button' aria-label='Upload documents' tabIndex={uploading ? -1 : 0} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.querySelector('input')?.click() } }}>
                {uploading ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Upload className='h-3.5 w-3.5' />
                )}
                {uploading ? 'Uploading…' : 'Upload documents'}
                <input
                  type='file'
                  multiple
                  className='hidden'
                  disabled={uploading}
                  onChange={(e) => {
                    void handleUpload(e.target.files)
                    e.target.value = ''
                  }}
                />
              </label>
            </Button>
          )}
          {isEditable && assessmentId && aiEnabled && (
            assessmentActionContainer ? createPortal(assessmentAction, assessmentActionContainer) : assessmentAction
          )}
          {isEditable && assessmentId && !aiEnabled && (
            <span className='font-body text-text-placeholder max-w-[220px] text-right text-[10px]'>
              AI assessment is available on the Pro plan
            </span>
          )}
        </div>
      </div>

      {completeness.total > 0 && (
        <div className='mb-3'>
          <div className='mb-1 flex items-center justify-between'>
            <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[0.5px] uppercase'>
              Requested documents
            </span>
            <span className='font-body text-text-body text-[11px] font-medium'>
              {completeness.received} of {completeness.total} received
              {completeness.waived > 0 ? ` · ${completeness.waived} waived` : ''}
              {selectedIds.length > 0
                ? ` · ${selectedIds.length} selected for this review`
                : ''}
            </span>
          </div>
        </div>
      )}

      {documentsQuery.isError ? <div role='alert' className='space-y-2 text-sm'><p>Could not load vendor documents.</p><Button variant='outline' onClick={() => void documentsQuery.refetch()}>Retry</Button></div> : documentsQuery.isLoading ? (
        <p className='font-body text-text-placeholder text-[11px]'>
          Loading documents…
        </p>
      ) : rows.length === 0 ? (
        <p className='font-body border-border text-text-placeholder rounded-sm border border-dashed px-2 py-4 text-center text-[11px]'>
          {isEditable
            ? 'No documents yet. Drop a SOC 2, DPA or contract here, or complete this assessment as an attestation without documents.'
            : 'No documents were attached to this assessment.'}
        </p>
      ) : (
        <>
          {requestRows.length > 0 && (
            <ul className='divide-border divide-y'>
              {requestRows.map((row) => renderRow(row))}
            </ul>
          )}
          {extraRows.length > 0 && (
            <>
              <div className='mt-3 mb-1 flex items-center gap-2'>
                <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[0.5px] uppercase'>
                  Also on file
                </span>
                <span className='font-body text-text-placeholder text-[10px]'>
                  {assessmentId ? 'not requested at this tier, tick to include anyway' : 'additional uploaded files'}
                </span>
              </div>
              <ul className='divide-border divide-y'>
                {extraRows.map((row) => renderRow(row))}
              </ul>
            </>
          )}
        </>
      )}

      <EvidenceFilePreviewDialog
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </section>
  )
}
